import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { toast } from "sonner";

const MFA_VERIFIED_KEY = "mfa_verified";

interface MFAEnforcementGuardProps {
  children: React.ReactNode;
}

export function MFAEnforcementGuard({ children }: MFAEnforcementGuardProps) {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Initialize from sessionStorage - if already verified, skip loading state entirely
  const [checking, setChecking] = useState(() => {
    // Immediately allow /my-profile without any loading
    if (location.pathname === "/my-profile") {
      return false;
    }
    // If already verified this session, skip loading
    return sessionStorage.getItem(MFA_VERIFIED_KEY) !== "true";
  });
  
  const [allowed, setAllowed] = useState(() => {
    // Immediately allow /my-profile
    if (location.pathname === "/my-profile") {
      return true;
    }
    // If already verified this session, allow immediately
    return sessionStorage.getItem(MFA_VERIFIED_KEY) === "true";
  });

  // Clear MFA verification on logout
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_OUT") {
        sessionStorage.removeItem(MFA_VERIFIED_KEY);
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  // Only run MFA check once on mount, not on every route change
  useEffect(() => {
    // Skip if already verified this session
    if (sessionStorage.getItem(MFA_VERIFIED_KEY) === "true") {
      setAllowed(true);
      setChecking(false);
      return;
    }
    
    // Skip check for my-profile page
    if (location.pathname === "/my-profile") {
      setAllowed(true);
      setChecking(false);
      return;
    }

    checkMFARequirement();
  }, []); // Empty dependency - only run once on mount

  const checkMFARequirement = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        // No session: fail closed and let route guards handle redirect to /auth.
        setAllowed(false);
        setChecking(false);
        navigate("/auth");
        return;
      }

      // Get current user's profile to find their company
      const { data: currentProfile, error: profileError } = await supabase
        .from("profiles")
        .select("company_name")
        .eq("id", user.id)
        .single();

      if (profileError || !currentProfile?.company_name) {
        // No company = no organization MFA requirement
        sessionStorage.setItem(MFA_VERIFIED_KEY, "true");
        setAllowed(true);
        setChecking(false);
        return;
      }

      // Find the owner of the organization to check if MFA is required
      const { data: ownerProfile, error: ownerError } = await supabase
        .from("profiles")
        .select("mfa_required")
        .eq("company_name", currentProfile.company_name)
        .eq("is_owner", true)
        .single();

      if (ownerError || !ownerProfile?.mfa_required) {
        // MFA not required by organization
        sessionStorage.setItem(MFA_VERIFIED_KEY, "true");
        setAllowed(true);
        setChecking(false);
        return;
      }

      // MFA is required - check if user has it set up
      const { data: factorsData, error: factorsError } = await supabase.auth.mfa.listFactors();

      if (factorsError) {
        console.error("Error checking MFA factors:", factorsError);
        // Fail closed — force the user to the security setup page rather than granting access.
        toast.error("Could not verify two-factor authentication. Please re-check your security settings.");
        navigate("/my-profile?tab=security");
        setAllowed(false);
        setChecking(false);
        return;
      }

      const verifiedFactors = factorsData?.totp?.filter(f => f.status === "verified") || [];

      if (verifiedFactors.length === 0) {
        // User needs to set up MFA - don't cache this, they need to be redirected each time
        toast.warning("Your organization requires two-factor authentication. Please set it up to continue.", {
          duration: 5000,
        });
        navigate("/my-profile?tab=security");
        setAllowed(false);
        setChecking(false);
        return;
      }

      // User has MFA set up - cache this for the session
      sessionStorage.setItem(MFA_VERIFIED_KEY, "true");
      setAllowed(true);
    } catch (error) {
      console.error("Error in MFA enforcement check:", error);
      // Fail closed on unexpected errors.
      setAllowed(false);
      navigate("/my-profile?tab=security");
    } finally {
      setChecking(false);
    }
  };

  if (checking) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!allowed) {
    return null;
  }

  return <>{children}</>;
}
